/**
 * Fullscreen overlay shown during the brief window when MimicContext is
 * flipping between actor and target. Without this, the dashboard's
 * permission map snaps and the user sees sidebar items / scope toggles
 * pop without explanation. The overlay holds the "switching to <name>"
 * beat and lets the underlying tree re-render under cover of the curtain.
 *
 * Self-renders nothing when no transition is in flight.
 */

import { Eye, Loader2 } from "lucide-react";
import { useMimic } from "@/contexts/MimicContext";

function getInitials(fullName: string | null, email: string | null): string {
    const name = (fullName ?? "").trim();
    if (name) {
        const parts = name.split(/\s+/).filter(Boolean);
        if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        return parts[0][0].toUpperCase();
    }
    if (email && email.length > 0) return email[0].toUpperCase();
    return "U";
}

export function MimicTransitionOverlay() {
    const { transition } = useMimic();
    if (!transition) return null;

    const isStarting = transition.phase === "starting";
    const target = transition.target;
    const displayName = target.full_name ?? target.email ?? "user";
    const initials = getInitials(target.full_name, target.email);

    return (
        <div
            className="fixed inset-0 z-[200] flex items-center justify-center bg-amber-500/95 backdrop-blur-sm animate-in fade-in duration-300"
            role="status"
            aria-live="polite"
        >
            <div className="bg-card text-card-foreground border border-border rounded-2xl shadow-2xl px-8 py-7 max-w-sm w-full mx-4 text-center space-y-4 animate-in zoom-in-95 fade-in duration-300">
                <div className="relative w-20 h-20 mx-auto">
                    {/* pulsing halo */}
                    <span className="absolute inset-0 rounded-full bg-amber-300/60 animate-ping" />
                    <span className="absolute inset-2 rounded-full bg-amber-200/70 animate-pulse" />
                    {/* avatar */}
                    <div className="relative w-full h-full rounded-full bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center text-white text-xl font-bold shadow-md">
                        {isStarting ? initials : <Eye className="w-7 h-7" />}
                    </div>
                </div>
                <div className="space-y-1.5">
                    <div className="text-base font-semibold text-amber-950">
                        {isStarting
                            ? `Switching to ${displayName}`
                            : `Returning to your view`}
                    </div>
                    <div className="text-xs text-muted-foreground">
                        {isStarting
                            ? `Loading the ${target.role_name} experience…`
                            : `Restoring your dashboard…`}
                    </div>
                </div>
                <div className="flex items-center justify-center gap-2 text-amber-700 text-xs">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    <span>Just a moment</span>
                </div>
            </div>
        </div>
    );
}
